import React, { createContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import authOffline from '../services/authOffline';
import offlineApi from '../services/offlineApi';
import { avatarService } from '../services/avatarService';
import { useAppNavigation } from '../hooks/useAppNavigation';
import logger from '../utils/logger';
import { getLoginErrorMessage, getRegisterErrorMessage } from '../utils/authErrorMessages';

// Create the auth context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useAppNavigation();

  // Handle Google OAuth callback
  const handleAuthCallback = async (code) => {
    try {
      const data = await authService.handleAuthCallback(code);
      if (data.access_token) {
        authService.setToken(data.access_token);
        await fetchUserData();
        navigate.to.HOME();
      }
    } catch (error) {
      console.error('Error in auth callback:', error);
      setError('Authentication failed. Please try again.');
    }
  };

  // Fetch current user data
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      let userData = null;
      
      try {
        // Try online auth first
        userData = await authService.getCurrentUser();
        // If successful, cache the auth data for offline use
        if (userData) {
          await authOffline.saveAuthData({
            user: userData,
            token: authService.getToken()
          });
        }
      } catch (error) {
        console.warn('Online auth failed, checking offline auth data...');
        const isOffline = !navigator.onLine;
        const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
        
        // If we're offline or in forced offline mode, try to get cached auth data
        if (isOffline || offlineModeEnabled) {
          const offlineAuthData = await authOffline.getAuthData();
          if (offlineAuthData?.user) {
            console.log('Using cached authentication data');
            userData = offlineAuthData.user;
            // Restore the token if available
            if (offlineAuthData.token) {
              authService.setToken(offlineAuthData.token);
            }
          }
        } else {
          // If we're online but auth failed for other reasons, propagate the error
          throw error;
        }
      }
      
      setCurrentUser(userData);
      setError(null);
      
      // Initialize logger with server-controlled debug settings
      if (userData) {
        logger.initFromServerSettings({
          debug_logging_enabled: userData.debug_logging_enabled
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setCurrentUser(null);
      // We don't set error here to avoid showing error messages when simply not logged in
    } finally {
      setIsLoading(false);
    }
  };

  // Login with email/password
  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const isOffline = !navigator.onLine;
      const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
      
      // If we're offline and offline mode is enabled, check for cached auth
      if (isOffline && offlineModeEnabled) {
        const offlineAuthData = await authOffline.getAuthData();
        if (offlineAuthData?.user && offlineAuthData?.token) {
          // In offline mode, we can't verify credentials
          // Instead, we're just checking if there's cached auth data
          // This is a simplified approach for offline testing
          authService.setToken(offlineAuthData.token);
          setCurrentUser(offlineAuthData.user);
          setError(null);
          return true;
        } else {
          setError('Cannot login while offline without previously cached credentials');
          return false;
        }
      }
      
      // Normal online login
      const data = await authService.login({ email, password });
      // Handle both v1 (access_token) and v2 (token) API responses
      const token = data.access_token || data.token;
      if (token) {
        authService.setToken(token);
        // Clear demo mode when logging in as regular user
        localStorage.removeItem('demo_mode');
        localStorage.removeItem('demo_info');
        localStorage.removeItem('demo_type');
        await fetchUserData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setError(getLoginErrorMessage(error));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Register new user
  const register = async (userData) => {
    try {
      setIsLoading(true);
      const data = await authService.register(userData);
      // Handle both v1 (access_token) and v2 (token) API responses
      const token = data.access_token || data.token;
      if (token) {
        authService.setToken(token);
        
        // Get user data from response if available, otherwise fetch it
        if (data.user) {
          // Ensure name field is set for UI consistency
          const processedUser = { ...data.user };
          if (processedUser.full_name && !processedUser.name) {
            processedUser.name = processedUser.full_name;
          }
          console.log('Setting user data from registration response:', processedUser);
          setCurrentUser(processedUser);
          
          // Also cache this data for offline use
          await authOffline.saveAuthData({
            user: processedUser,
            token: token
          });
        } else {
          // Fallback to fetching user data
          await fetchUserData();
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Registration error:', error);
      setError(getRegisterErrorMessage(error));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout user
  const logout = async () => {
    authService.removeToken();
    await authOffline.clearAuthData();
    setCurrentUser(null);
    // Reset logger to default level on logout
    logger.reset();
    navigate.to.LOGIN();
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setIsLoading(true);
      const updatedData = await authService.updateUserProfile(userData);
      setCurrentUser(updatedData);
      return true;
    } catch (error) {
      console.error('Profile update error:', error);
      setError('Failed to update profile. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Google login
  const googleLogin = () => {
    const url = authService.getGoogleLoginUrl();
    // Use the location.assign method for navigation
    window.location.assign(url);
  };

  // Apple login
  const appleLogin = () => {
    const url = authService.getAppleLoginUrl();
    // Use the location.assign method for navigation
    window.location.assign(url);
  };

  // Check if user is authenticated on initial load
  useEffect(() => {
    const checkAuth = async () => {
      // Check for admin impersonation token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const impersonateToken = urlParams.get('impersonate_token');
      
      if (impersonateToken) {
        console.log('[Auth] Admin impersonation token detected');
        // Set the impersonation token and fetch user data
        authService.setToken(impersonateToken);
        // Remove the token from URL for security
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        await fetchUserData();
        return;
      }
      
      const token = authService.getToken();
      if (token) {
        fetchUserData();
      } else {
        // If no token in localStorage, check for offline auth data
        const isOffline = !navigator.onLine;
        if (isOffline) {
          const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
          if (offlineModeEnabled) {
            const offlineAuthData = await authOffline.getAuthData();
            if (offlineAuthData?.user && offlineAuthData?.token) {
              // Restore from offline auth
              authService.setToken(offlineAuthData.token);
              setCurrentUser(offlineAuthData.user);
            }
          }
        }
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  // Refresh user avatar
  const refreshAvatar = async () => {
    try {
      if (currentUser) {
        const avatarData = await avatarService.refreshUserAvatar();
        // Update current user with new avatar
        setCurrentUser(prev => ({
          ...prev,
          profile_picture: avatarData.avatar_url
        }));
        return avatarData.avatar_url;
      }
    } catch (error) {
      console.error('Error refreshing avatar:', error);
      throw error;
    }
  };

  // Get user avatar with fallback
  const getUserAvatar = async () => {
    try {
      if (currentUser) {
        const avatarData = await avatarService.getUserAvatar();
        return avatarData.avatar_url;
      }
    } catch (error) {
      console.error('Error getting avatar:', error);
      // Return fallback avatar
      if (currentUser) {
        return avatarService.getInitialsAvatarUrl(
          currentUser.name || currentUser.full_name, 
          currentUser.email
        );
      }
    }
  };

  // Providing authentication context to all components
  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLoading,
        error,
        login,
        logout,
        register,
        googleLogin,
        appleLogin,
        handleAuthCallback,
        updateProfile,
        refreshAvatar,
        getUserAvatar,
        setError, // Allow components to clear errors
        fetchUserData, // Export for OAuth callback handling
        // Additional properties for offline mode
        isOffline: !navigator.onLine
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
